import React, { useEffect, useState } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import TourCalendarPage from "./tour-calendar/TourCalendarPage";
import { getPackageforDestination, getPopularDestinations } from "../../actions/tour/tourAction";
import { slugify } from "../../helpers/commonHelper";
import useSEO from "../../hooks/useSEO";

const FALLBACK_IMG = "https://placehold.co/1200x500?text=Destination";

export default function DestinationDetail() {
  const { destinationId } = useParams();
  const { state, search } = useLocation();
  const navigate = useNavigate();
  const { setSEO } = useSEO();

  const [destination, setDestination] = useState(state?.destination || null);
  const [packages, setPackages] = useState([]);
  const [loadingDestination, setLoadingDestination] = useState(!state?.destination);
  const [loadingPackages, setLoadingPackages] = useState(true);

  // Fetching destination detail if missing from state
  useEffect(() => {
    if (!destination && destinationId) {
      setLoadingDestination(true);
      getPopularDestinations((data) => {
        if (Array.isArray(data)) {
          const found = data.find((d) => d.DestinationID.toString() === destinationId.toString());
          if (found) {
            setDestination(found);
            setSEO(`Explore the best of ${found.DestinationName} | Yatra Tickets`, found.Description, found.ImageUrl);
          }
        }
        setLoadingDestination(false);
      });
    } else if (destination) {
      setSEO(`Explore the best of ${destination.DestinationName} | Yatra Tickets`, destination.Description, destination.ImageUrl);
    }
  }, [destinationId, destination, setSEO]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fetch packages for this destination from the database
  useEffect(() => {
    if (destinationId) {
      setLoadingPackages(true);
      getPackageforDestination(destinationId, (data) => {
        setPackages(data || []);
        setLoadingPackages(false);
      });
    }
  }, [destinationId]);


  const name = destination?.DestinationName || "Destination";
  const img = destination?.ImageUrl?.trim() || FALLBACK_IMG;

  return (
    <div className="bg-background">
      <div className="container mx-auto px-4 py-6 space-y-6">
        {/* Back */}
        <div className="flex items-center justify-between">
          <Button variant="outline" onClick={() => navigate(-1)}>
            Back
          </Button>
        </div>

        {/* Header */}
        <div className="overflow-hidden rounded-xl border bg-card shadow-sm">
          <div className="h-56 md:h-72 w-full bg-muted overflow-hidden">
            <img
              src={img}
              alt={name}
              className="h-full w-full object-cover"
              onError={(e) => {
                e.currentTarget.src = FALLBACK_IMG;
              }}
            />
          </div>

          <div className="p-5 space-y-2">
            {loadingDestination ? (
              <>
                <Skeleton className="h-6 w-1/2" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-5/6" />
              </>
            ) : (
              <>
                <h1 className="text-xl md:text-2xl font-semibold tracking-tight">
                  {destination?.DestinationName}
                </h1>

                {destination?.Description ? (
                  <p className="text-sm md:text-base text-muted-foreground">
                    {destination.Description}
                  </p>
                ) : null}

                {destination?.Article ? (
                  <div className="pt-2">
                    <div
                      className="text-sm text-muted-foreground"
                      dangerouslySetInnerHTML={{ __html: destination.Article }}
                    />
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>

        {/* Body: Packages */}
        <div className="space-y-3">
          <h2 className="text-lg md:text-xl font-semibold tracking-tight">
            Available Packages
          </h2>

          {loadingPackages ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="rounded-xl border bg-card p-4 space-y-2">
                  <Skeleton className="h-40 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-full" />
                  <Skeleton className="h-8 w-24" />
                </div>
              ))}
            </div>
          ) : packages.length === 0 ? (
            <div className="rounded-xl border bg-card p-5 text-sm text-muted-foreground">
              No active packages found for this destination.
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {packages.map((p) => {
                // Get the first image from FileList, or use a fallback
                const imageUrl = p?.FileList?.[0]?.FileURL || "https://placehold.co/800x500?text=Tour+Package";

                return (
                  <div
                    key={p?.PackageID}
                    className="overflow-hidden rounded-xl border bg-card shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="h-40 w-full bg-muted overflow-hidden">
                      <img
                        src={imageUrl}
                        alt={p?.Title || "Package"}
                        className="h-full w-full object-cover"
                        loading="lazy"
                      />
                    </div>

                    <div className="p-4 space-y-2">
                      <h3 className="font-semibold line-clamp-1">
                        {p?.Title}
                      </h3>

                      {p?.Summary ? (
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {p.Summary}
                        </p>
                      ) : null}

                      <div className="flex items-center justify-between pt-2">
                        <span className="text-sm font-medium">
                          {p?.PriceFrom != null ? `From ${p.PriceFrom}` : ""}
                        </span>

                        <Button
                          size="sm"
                          onClick={() => {
                            // Navigate to your existing package detail page
                            // Keep the same `search` string
                            navigate(
                              {
                                pathname: `/tour/package/${p.PackageID}/${slugify(p.Title || "package")}`,
                                search: search,
                              },
                              { state: { package: p } }
                            );
                          }}
                        >
                          View
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        {/* <div className="space-y-3">
          <TourCalendarPage />
        </div> */}
      </div>
    </div>
  );
}
